import {
    ArrowLeftOutlined, DeleteOutlined, EditOutlined,
} from '@ant-design/icons';
import {
    useMutation, useQuery,
} from '@tanstack/react-query';
import {
    Button, Card, Descriptions, Flex, Grid, theme, Typography,
} from 'antd';
import {
    useCallback, useMemo, useState,
} from 'react';
import { Helmet } from 'react-helmet';
import {
    Link,
    useNavigate,
    useParams,
} from 'react-router-dom';

import {
    usersMutations, usersQueries,
} from '@/api';
import {
    DeleteProjectModal, getActionControlStyleVars, RoleGuard,
} from '@/components';
import {
    AppRoute, UserRole, UserRoles,
} from '@/constants';
import { useUserStore } from '@/stores';
import {
    canDeleteUser, canEditUser,
} from '@/utils';

export const UserDetailPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { user: currentUser } = useUserStore();
    const { token } = theme.useToken();
    const screens = Grid.useBreakpoint();
    const isMobileDetailLayout = screens.lg === false;
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);

    const detailCardStyles = useMemo(() => {
        if (!isMobileDetailLayout) {
            return undefined;
        }
        return {
            header: {
                paddingTop: 16,
                paddingBottom: 16,
                paddingLeft: 16,
                paddingRight: 16,
                overflow: 'visible',
            },
            title: {
                display: 'block',
                overflow: 'visible',
                whiteSpace: 'normal',
                textOverflow: 'unset',
                width: '100%',
                maxWidth: '100%',
            },
            body: {
                padding: 16,
            },
        };
    }, [isMobileDetailLayout]);

    const { data: user, isLoading } = useQuery(usersQueries.getOneUser(id || ''));

    const deleteMutationOptions = useMemo(
        () => user
            ? usersMutations.delete(user.id)
            : usersMutations.delete(''),
        [user],
    );

    const { mutate: deleteUser, isPending: isDeleting } = useMutation(deleteMutationOptions);

    const handleDelete = useCallback(() => {
        if (user) {
            setDeleteModalOpen(true);
        }
    }, [user]);

    const handleDeleteConfirm = useCallback(() => {
        if (user) {
            deleteUser(undefined, {
                onSuccess: () => {
                    setDeleteModalOpen(false);
                    navigate(AppRoute.Users);
                },
            });
        }
    }, [user, deleteUser, navigate]);

    const handleDeleteCancel = useCallback(() => {
        setDeleteModalOpen(false);
    }, []);

    return (
        <>
            <Helmet>
                <title>Деталі користувача</title>
            </Helmet>

            <Card
                loading={isLoading}
                className="detail-page-card"
                styles={detailCardStyles}
                title={
                    <Flex
                        vertical={isMobileDetailLayout}
                        align={isMobileDetailLayout ? 'stretch' : 'center'}
                        justify="space-between"
                        gap="middle"
                        wrap="wrap"
                        style={{ width: '100%' }}
                    >
                        <Flex align="center" gap="middle" style={{ minWidth: 0, flex: isMobileDetailLayout ? undefined : 1 }}>
                            <Link
                                to={AppRoute.Users}
                                aria-label="Назад"
                                className="app-action-control app-action-control--icon"
                                style={getActionControlStyleVars(token, {
                                    variant: 'text',
                                    bordered: false,
                                    iconOnly: true,
                                })}
                            >
                                <ArrowLeftOutlined style={{ fontSize: 16 }} />
                            </Link>
                            <Typography.Title
                                level={4}
                                ellipsis={false}
                                style={{ margin: 0, whiteSpace: 'normal', wordBreak: 'break-word' }}
                            >
                                {user?.nickname || user?.email || 'Деталі користувача'}
                            </Typography.Title>
                        </Flex>
                        <RoleGuard roles={[UserRole.SuperAdmin, UserRole.Admin]}>
                            <Flex gap="middle" wrap="wrap" style={{ marginLeft: isMobileDetailLayout ? 0 : 'auto', justifyContent: isMobileDetailLayout ? 'flex-start' : undefined }}>
                                {canEditUser(currentUser, user || null) && (
                                    <Link
                                        to={`${AppRoute.Users}/${id}/edit`}
                                        className="app-action-control"
                                        style={getActionControlStyleVars(token, {
                                            variant: 'primary',
                                        })}
                                    >
                                        <EditOutlined style={{ fontSize: 16 }} />
                                        Редагувати
                                    </Link>
                                )}
                                {canDeleteUser(currentUser, user || null) && (
                                    <Button
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={handleDelete}
                                        disabled={isDeleting}
                                    >
                                        Видалити
                                    </Button>
                                )}
                            </Flex>
                        </RoleGuard>
                    </Flex>
                }
            >
                <Descriptions column={1} bordered>
                    <Descriptions.Item label="Нікнейм">
                        {user?.nickname || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Ім'я">
                        {user?.name || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Email">
                        {user?.email || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Роль">
                        {user?.role ? UserRoles[user?.role] : '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Коментар">
                        {user?.comment || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Проекти">
                        {user?.projects?.length || 0}
                    </Descriptions.Item>
                    <Descriptions.Item label="Скрипти">
                        {user?.scripts?.length || 0}
                    </Descriptions.Item>
                    <Descriptions.Item label="Створено">
                        {user?.created_at || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Оновлено">
                        {user?.updated_at || '-'}
                    </Descriptions.Item>
                </Descriptions>
            </Card>

            <DeleteProjectModal
                open={deleteModalOpen}
                entityName={user?.nickname || user?.email}
                entityType="user"
                onConfirm={handleDeleteConfirm}
                onCancel={handleDeleteCancel}
                loading={isDeleting}
            />
        </>
    );
};
